/**
 * Lecture des tags de rotation ID3 (« ROTATE:<deg>|TS:<ms> », écrits par
 * l'enregistreur du téléphone) dans un flux MPEG-TS déchiffré, sans
 * bibliothèque. Les instants sont relatifs à la première image vidéo, en
 * secondes : la même référence que la conversion MP4.
 */
const PACKET = 188;

function readPts(bytes, offset) {
    // 33 bits répartis sur 5 octets ; multiplications pour rester exact au-delà de 2^31.
    return ((bytes[offset] >> 1) & 0x07) * 1073741824
        + bytes[offset + 1] * 4194304
        + (bytes[offset + 2] >> 1) * 32768
        + bytes[offset + 3] * 128
        + (bytes[offset + 4] >> 1);
}

function pesPts(bytes, start, end) {
    if (end - start < 14 || bytes[start] !== 0 || bytes[start + 1] !== 0 || bytes[start + 2] !== 1) return null;
    return (bytes[start + 7] & 0x80) ? readPts(bytes, start + 9) : null;
}

export const cardinalRotation = degrees => (Math.round((((Number(degrees) % 360) + 360) % 360) / 90) * 90) % 360;

export function rotationCuesFromTs(input) {
    const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    let pmtPid = null;
    let videoPid = null;
    let metadataPid = null;
    let firstVideoPts = null;
    const raw = [];
    const latin1 = new TextDecoder('latin1');
    for (let p = 0; p + PACKET <= bytes.length; p += PACKET) {
        if (bytes[p] !== 0x47) continue;
        const pid = ((bytes[p + 1] & 0x1f) << 8) | bytes[p + 2];
        const unitStart = (bytes[p + 1] & 0x40) !== 0;
        const adaptation = (bytes[p + 3] >> 4) & 0x03;
        let offset = p + 4;
        if (adaptation & 0x02) offset += 1 + bytes[offset];
        if (!(adaptation & 0x01) || offset >= p + PACKET) continue;
        const end = p + PACKET;
        if (pid === 0 && unitStart && pmtPid === null) {
            const table = offset + 1 + bytes[offset];
            // Premier programme (hors réseau) : PID de la PMT.
            for (let e = table + 8; e + 4 <= table + 3 + (((bytes[table + 1] & 0x0f) << 8) | bytes[table + 2]) - 4; e += 4) {
                const program = (bytes[e] << 8) | bytes[e + 1];
                if (program !== 0) { pmtPid = ((bytes[e + 2] & 0x1f) << 8) | bytes[e + 3]; break; }
            }
        } else if (pid === pmtPid && unitStart && videoPid === null) {
            const table = offset + 1 + bytes[offset];
            const sectionEnd = table + 3 + (((bytes[table + 1] & 0x0f) << 8) | bytes[table + 2]) - 4;
            let e = table + 12 + (((bytes[table + 10] & 0x0f) << 8) | bytes[table + 11]);
            while (e + 5 <= sectionEnd) {
                const type = bytes[e];
                const elementaryPid = ((bytes[e + 1] & 0x1f) << 8) | bytes[e + 2];
                if ((type === 0x1b || type === 0x24) && videoPid === null) videoPid = elementaryPid;
                if (type === 0x15) metadataPid = elementaryPid;
                e += 5 + (((bytes[e + 3] & 0x0f) << 8) | bytes[e + 4]);
            }
        } else if (unitStart && pid === videoPid) {
            const pts = pesPts(bytes, offset, end);
            if (pts !== null && (firstVideoPts === null || pts < firstVideoPts)) firstVideoPts = pts;
        } else if (unitStart && pid === metadataPid) {
            const pts = pesPts(bytes, offset, end);
            const match = /ROTATE:(-?\d+(?:\.\d+)?)/i.exec(latin1.decode(bytes.subarray(offset, end)));
            if (pts !== null && match) raw.push({ pts, deg: Number(match[1]) });
        }
    }
    const base = firstVideoPts ?? (raw.length ? raw[0].pts : 0);
    return raw
        .map(cue => ({ t: (cue.pts - base) / 90000, deg: ((cue.deg % 360) + 360) % 360 }))
        .sort((a, b) => a.t - b.t);
}

/** Angle du dernier tag à l'instant t (le premier tag vaut aussi avant lui). */
export function rotationAt(cues, t) {
    if (!cues.length) return 0;
    let low = 0;
    let high = cues.length - 1;
    if (t < cues[0].t) return cues[0].deg;
    while (low < high) {
        const mid = (low + high + 1) >> 1;
        if (cues[mid].t <= t) low = mid; else high = mid - 1;
    }
    return cues[low].deg;
}
